import { getRandomInt } from "../Shared/Helpers.tsx";
import {
    LINE_PLOT_MAX_STACK,
    LinePlotMarkOptions,
    LinePlotQuestionOptions,
    LinePlotScaleOptions,
    LinePlotSpec,
    ProblemData,
    ProblemFormatOptions,
} from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";
import { PrintedLanguage, printedLanguageOf } from "./PrintedLanguage.ts";
import { linePlotContexts as enContexts, linePlotPrompts as en } from "../Files/Prompts/en/linePlots.ts";
import { linePlotContexts as esContexts, linePlotPrompts as es } from "../Files/Prompts/es/linePlots.ts";
import { linePlotContexts as frContexts, linePlotPrompts as fr } from "../Files/Prompts/fr/linePlots.ts";

const PROMPTS: Record<PrintedLanguage, typeof en> = { en, es, fr };
const CONTEXTS: Record<PrintedLanguage, typeof enContexts> = { en: enContexts, es: esContexts, fr: frContexts };

/**
 * Line plots and dot plots (ADR 0084). A plot is a number line with a mark
 * stacked above each tick once for every time that value is in the data. Like
 * a bar graph (ADR 0040), one plot is asked several questions: every question
 * in a group carries the same `problemGroup.linePlot`, which
 * `PdfGenerator/Page.tsx` draws once above the group's rows.
 *
 * Every rule ADR 0084 §3 sets for "exactly one reading" is met by how the data
 * is built, never by drawing at random and checking:
 *
 * - Marks sit only on ticks, and the generator never writes a value that is
 *   not a tick: the data is a count per tick, not a list of values.
 * - At most `MAX_STACK` marks a tick.
 * - The tallest stack is drawn first and every other stack below it, so
 *   exactly one value is most common.
 * - When the sheet can ask for a median, the count is made odd by moving one
 *   non-tallest stack up or down by one, so the median is one mark's value.
 * - The two end ticks are always empty, so the least and greatest values are
 *   read off marks, not off the ends of the axis.
 */

/** Ticks on every plot, every one labelled (ADR 0084 §4). */
export const LINE_PLOT_TICKS = 9;

/** The most marks one tick carries (ADR 0084 §3). */
const MAX_STACK = LINE_PLOT_MAX_STACK;

/** The least marks a plot holds, so a total is worth asking. */
const MIN_MARKS = 10;

/** Up to this many questions about one plot (ADR 0084 §5). */
export const QUESTIONS_PER_PLOT = 4;

/** Ticks per whole unit: whole numbers, halves, quarters, eighths. */
export function denominatorOf(scale: LinePlotScaleOptions): number {
    switch (scale) {
        case LinePlotScaleOptions.Whole:
            return 1;
        case LinePlotScaleOptions.Quarters:
            return 4;
        case LinePlotScaleOptions.Eighths:
            return 8;
        default:
            return 2;
    }
}

/**
 * A value written in the plot's own unit, not simplified (ADR 0084 §4):
 * `steps` ticks of 1/`denominator` past `start`. On a quarters plot from 1,
 * step 2 is "1 2/4", never "1 1/2", so every label and every answer on a sheet
 * shares one denominator.
 */
export function linePlotLabel(start: number, denominator: number, steps: number): string {
    const numerator = start * denominator + steps;
    if (denominator === 1) {
        return String(numerator);
    }
    const whole = Math.floor(numerator / denominator);
    const part = numerator % denominator;
    if (part === 0) {
        return String(whole);
    }
    return whole === 0 ? `${part}/${denominator}` : `${whole} ${part}/${denominator}`;
}

/** How many marks a plot holds. */
export function totalMarks(counts: number[]): number {
    return counts.reduce((sum, count) => sum + count, 0);
}

/** The tick index of the median mark. Only meaningful on an odd count. */
export function medianIndex(counts: number[]): number {
    const middle = (totalMarks(counts) + 1) / 2;
    let seen = 0;
    for (let i = 0; i < counts.length; i++) {
        seen += counts[i];
        if (seen >= middle) {
            return i;
        }
    }
    return counts.length - 1;
}

/**
 * A count per tick that meets ADR 0084 §3 by construction. Marks run from a
 * tick in [1, 3] to one in [lo + 4, 7]: both end ticks stay empty, and the data
 * spans at least five ticks so a range question is not trivial.
 */
export function buildCounts(oddTotal: boolean): number[] {
    const counts = new Array(LINE_PLOT_TICKS).fill(0);
    const lo = getRandomInt(1, 3);
    const hi = getRandomInt(lo + 4, LINE_PLOT_TICKS - 2);
    const tallest = getRandomInt(3, MAX_STACK);
    const modeAt = getRandomInt(lo, hi);

    for (let i = lo; i <= hi; i++) {
        if (i === modeAt) {
            counts[i] = tallest;
        } else if (i === lo || i === hi) {
            // The ends carry at least one mark, or they would not be the ends.
            counts[i] = getRandomInt(1, tallest - 1);
        } else {
            counts[i] = getRandomInt(0, tallest - 1);
        }
    }

    // Too few marks to be worth totalling: raise stacks below the tallest one,
    // never to its height, so the mode stays unique.
    const raisable = () => counts
        .map((count, i) => ({ count, i }))
        .filter(({ count, i }) => i >= lo && i <= hi && i !== modeAt && count < tallest - 1);
    while (totalMarks(counts) < MIN_MARKS && raisable().length > 0) {
        const pool = raisable();
        counts[pool[getRandomInt(0, pool.length - 1)].i]++;
    }

    if (oddTotal && totalMarks(counts) % 2 === 0) {
        // Move one stack that is not the tallest by one mark. A stack may go
        // up while it stays below the tallest, and down only while it keeps
        // its floor (one mark at an end) and the plot keeps MIN_MARKS. One can
        // always move: at MIN_MARKS some stack has room to go up, because at
        // least four stacks share the span and four full ones (each at least
        // 2, beside a tallest of at least 3) already make 11.
        const canDrop = totalMarks(counts) > MIN_MARKS;
        const movable = counts
            .map((count, i) => ({ count, i }))
            .filter(({ i }) => i >= lo && i <= hi && i !== modeAt)
            .filter(({ count, i }) => count < tallest - 1 || (canDrop && count > (i === lo || i === hi ? 1 : 0)));
        const { count, i } = movable[getRandomInt(0, movable.length - 1)];
        counts[i] = count < tallest - 1 ? count + 1 : count - 1;
    }

    return counts;
}

/** A freshly drawn plot: its data, its line and its title. */
function buildPlot(
    scale: LinePlotScaleOptions,
    mark: LinePlotMarkOptions,
    oddTotal: boolean,
    language: PrintedLanguage,
    usedTitles: Set<string>
): { spec: LinePlotSpec; denominator: number } {
    const denominator = denominatorOf(scale);
    const all = scale === LinePlotScaleOptions.Whole ? CONTEXTS[language].counted : CONTEXTS[language].measured;
    // Two plots on one page about the same thing read as a mistake, so a page
    // uses each context once while it has unused ones left.
    const fresh = all.filter((context) => !usedTitles.has(context.title));
    const contexts = fresh.length > 0 ? fresh : all;
    const context = contexts[getRandomInt(0, contexts.length - 1)];
    usedTitles.add(context.title);
    const start = getRandomInt(context.start[0], context.start[1]);
    const labels = Array.from({ length: LINE_PLOT_TICKS }, (_, i) => linePlotLabel(start, denominator, i));
    return {
        spec: { title: context.title, labels, counts: buildCounts(oddTotal), mark },
        denominator,
    };
}

/** The question types a sheet draws from, in rotation order. */
export function linePlotQuestionTypes(question: LinePlotQuestionOptions): LinePlotQuestionOptions[] {
    if (question === LinePlotQuestionOptions.Mixed) {
        return [
            LinePlotQuestionOptions.ReadOne,
            LinePlotQuestionOptions.Difference,
            LinePlotQuestionOptions.Total,
            LinePlotQuestionOptions.MostCommon,
            LinePlotQuestionOptions.Range,
        ];
    }
    if (question === LinePlotQuestionOptions.CenterAndSpread) {
        return [
            LinePlotQuestionOptions.MostCommon,
            LinePlotQuestionOptions.Median,
            LinePlotQuestionOptions.Range,
        ];
    }
    return [question];
}

/**
 * Question types with exactly one true answer per plot. A sheet pinned to one
 * of them draws a plot per question (ADR 0084 §5), and `perPageOptions.ts`
 * offers that sheet its own, lower, counts.
 */
export const SINGLE_ANSWER_LINE_PLOT_QUESTIONS: LinePlotQuestionOptions[] = [
    LinePlotQuestionOptions.Total,
    LinePlotQuestionOptions.MostCommon,
    LinePlotQuestionOptions.Range,
    LinePlotQuestionOptions.Median,
];

/** What a group has already asked about its plot, so the next question is new (ADR 0040 §3). */
interface GroupUsage {
    once: Set<LinePlotQuestionOptions>;
    pairs: Set<string>;
    values: Set<number>;
}

function tryBuildQuestion(
    type: LinePlotQuestionOptions,
    spec: LinePlotSpec,
    denominator: number,
    used: GroupUsage,
    language: PrintedLanguage
): { prompt: string; answer: string; commit: () => void } | null {
    const t = PROMPTS[language];
    const { counts, labels, mark } = spec;
    const marked = counts.map((count, i) => ({ count, i })).filter(({ count }) => count > 0);

    if (SINGLE_ANSWER_LINE_PLOT_QUESTIONS.includes(type) && used.once.has(type)) {
        return null;
    }
    const commitOnce = () => { used.once.add(type); };

    switch (type) {
        case LinePlotQuestionOptions.Total:
            return { prompt: t.total(mark), answer: String(totalMarks(counts)), commit: commitOnce };
        case LinePlotQuestionOptions.MostCommon: {
            const tallest = Math.max(...counts);
            return { prompt: t.mostCommon(), answer: labels[counts.indexOf(tallest)], commit: commitOnce };
        }
        case LinePlotQuestionOptions.Range: {
            const least = marked[0].i;
            const greatest = marked[marked.length - 1].i;
            return { prompt: t.range(), answer: linePlotLabel(0, denominator, greatest - least), commit: commitOnce };
        }
        case LinePlotQuestionOptions.Median:
            return { prompt: t.median(), answer: labels[medianIndex(counts)], commit: commitOnce };
        case LinePlotQuestionOptions.Difference: {
            const pairs: [number, number][] = [];
            for (const a of marked) {
                for (const b of marked) {
                    if (a.count > b.count && !used.pairs.has(`${Math.min(a.i, b.i)}:${Math.max(a.i, b.i)}`)) {
                        pairs.push([a.i, b.i]);
                    }
                }
            }
            if (pairs.length === 0) return null;
            const [more, fewer] = pairs[getRandomInt(0, pairs.length - 1)];
            return {
                prompt: t.difference(labels[more], labels[fewer], mark),
                answer: String(counts[more] - counts[fewer]),
                commit: () => used.pairs.add(`${Math.min(more, fewer)}:${Math.max(more, fewer)}`),
            };
        }
        default: {
            // ReadOne.
            const available = marked.filter(({ i }) => !used.values.has(i));
            if (available.length === 0) return null;
            const { count, i } = available[getRandomInt(0, available.length - 1)];
            return {
                prompt: t.readOne(labels[i], mark),
                answer: String(count),
                commit: () => used.values.add(i),
            };
        }
    }
}

/**
 * One plot and up to `want` distinct questions about it, all tagged with the
 * same group so the page draws the plot once. Comes back short when the
 * allowed types run out of new questions for this plot (a pinned `Total`
 * sheet has one per plot); the caller draws a fresh plot for the rest.
 * `startTypeIndex` carries a Mixed sheet's rotation across groups, for the
 * reason `buildBarGraphGroup` gives.
 */
export function buildLinePlotGroup(
    settings: Pick<ISettingsProps, 'LinePlotQuestion' | 'LinePlotScale' | 'LinePlotMark'>,
    want: number,
    groupKey: string,
    startKey: number,
    startTypeIndex: number,
    language: PrintedLanguage,
    usedTitles: Set<string> = new Set()
): { problems: ProblemData[]; nextKey: number; nextTypeIndex: number } {
    const types = linePlotQuestionTypes(settings.LinePlotQuestion ?? LinePlotQuestionOptions.Mixed);
    const oddTotal = types.includes(LinePlotQuestionOptions.Median);
    const { spec, denominator } = buildPlot(
        settings.LinePlotScale ?? LinePlotScaleOptions.Halves,
        settings.LinePlotMark ?? LinePlotMarkOptions.X,
        oddTotal,
        language,
        usedTitles
    );
    const used: GroupUsage = { once: new Set(), pairs: new Set(), values: new Set() };
    const problems: ProblemData[] = [];
    let key = startKey;
    let typeIndex = startTypeIndex;
    let stalled = 0;

    while (problems.length < want && stalled < types.length) {
        const type = types[typeIndex % types.length];
        typeIndex++;
        const picked = tryBuildQuestion(type, spec, denominator, used, language);
        if (!picked) {
            stalled++;
            continue;
        }
        stalled = 0;
        picked.commit();
        problems.push({
            key: key++,
            numbers: [],
            operator: "",
            display: [picked.prompt, picked.answer],
            linePlotQuestion: type,
            problemGroup: { key: groupKey, format: ProblemFormatOptions.LinePlots, linePlot: spec },
        });
    }

    return { problems, nextKey: key, nextTypeIndex: typeIndex % types.length };
}

/** A page-shaped set of line-plot problems, up to `QUESTIONS_PER_PLOT` to a plot. */
export function generateLinePlotPages(settings: ISettingsProps): ProblemData[][] | null {
    const perPage = settings.NumberOfProblemsPerPage;
    const language = printedLanguageOf(settings);
    const pages: ProblemData[][] = [];
    let nextKey = 1;

    for (let page = 0; page < settings.NumberOfPages; page++) {
        const problems: ProblemData[] = [];
        let groupIndex = 0;
        let typeIndex = 0;
        const usedTitles = new Set<string>();
        // Every group yields at least one question about a fresh plot, so this
        // always makes progress; the guard turns a broken guarantee into a
        // short page rather than a hung tab.
        const guardLimit = perPage * 4 + 10;
        let guard = 0;

        while (problems.length < perPage && guard < guardLimit) {
            guard++;
            const want = Math.min(QUESTIONS_PER_PLOT, perPage - problems.length);
            const group = buildLinePlotGroup(
                settings,
                want,
                `line-plot:${page}:${groupIndex}`,
                nextKey,
                typeIndex,
                language,
                usedTitles
            );
            if (group.problems.length === 0) {
                break;
            }
            nextKey = group.nextKey;
            typeIndex = group.nextTypeIndex;
            problems.push(...group.problems);
            groupIndex++;
        }

        pages.push(problems);
    }

    return pages.some((page) => page.length > 0) ? pages : null;
}
